/**
 * QuestionItem Component
 * Renders a question (essay, multiple choice, or true/false) with answer input and validation.
 * Applies best practices for readability, maintainability, and accessibility.
 */

import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DOMPurify from 'dompurify';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  CheckboxGroup,
  RadioGroup,
  Radio,
  Checkbox,
  Text,
  Alert,
  AlertIcon,
  useColorModeValue,
  Textarea,
} from '@chakra-ui/react';
import {
  ESSAY_ID,
  MULTIPLE_CHOICE_ID,
  TRUE_OR_FALSE_ID,
} from 'utils/constants/question';
import FormikFormControl from 'components/common/forms/FormikFormControl';

/**
 * Renders a single question item with answer input and validation.
 * @param {Object} props
 * @param {number} props.position - The position/index of the question in the list.
 * @param {Object} props.question - The question object containing metadata and content.
 * @param {Array} props.selectedValue - The currently selected answer(s).
 * @param {Function} props.handleSelectAnswer - Callback to update the selected answer(s).
 */
function QuestionItem({
  position,
  question,
  selectedValue,
  handleSelectAnswer,
}) {
  const {
    questionHtml,
    questionType,
    hasMultipleAnswer,
    multipleChoiceChoicesJson,
  } = question;

  // State for essay answer and validation error
  const [essayValue, setEssayValue] = useState(selectedValue[0] || '');
  const [essayError, setEssayError] = useState('');

  // Update essay value when selectedValue changes (e.g., reset)
  useEffect(() => {
    setEssayValue(selectedValue[0] || '');
  }, [selectedValue]);

  /**
   * Handles changes in the essay textarea.
   * Validates input length and updates parent with new value.
   */
  const handleEssayChange = ({ target: { value } }) => {
    setEssayValue(value);
    if (value.length === 0) {
      setEssayError('Answer is required.');
    } else if (value.length > 20000) {
      setEssayError('Answer must be between 1 and 20,000 characters.');
    } else {
      setEssayError('');
      handleSelectAnswer(question, [value]);
    }
  };

  /**
   * Parses the multiple choice options from JSON.
   * Returns null if parsing fails.
   */
  const getParsedOptions = () => {
    try {
      return JSON.parse(multipleChoiceChoicesJson);
    } catch {
      return null;
    }
  };

  const parsedOptions = getParsedOptions();

  // Color mode values for consistent theming
  const borderColor = useColorModeValue('gray.200', 'gray.500');
  const activeBorderColor = useColorModeValue('red', 'teal');
  const hoverColor = useColorModeValue('red.100', 'teal');
  const optionsSelectColor = useColorModeValue('red', 'teal');

  /**
   * Handles selection/deselection of a checkbox option (multiple answers).
   * @param {Object} currentQuestion - The current question object.
   * @param {string} option - The option value to toggle.
   */
  const handleCheckboxParentClick = (currentQuestion, option) => {
    if (selectedValue && selectedValue.includes(option)) {
      handleSelectAnswer(
        currentQuestion,
        selectedValue.filter((v) => v !== option)
      );
    } else {
      handleSelectAnswer(currentQuestion, [...selectedValue, option]);
    }
  };

  /**
   * Renders an error alert for option parsing failures.
   */
  const renderOptionsError = () => (
    <Alert status="error">
      <AlertIcon />
      An error occurred while displaying the question&apos;s options.
    </Alert>
  );

  /**
   * Returns a stable key for option rendering.
   * Prefers option value, falls back to index if necessary.
   */
  const getOptionKey = (choice, idx) => choice.option || choice.value || idx;

  return (
    <Box>
      {/* Question position label */}
      <Text fontSize="xs" as="samp">
        Question {position}
      </Text>

      {/* Render question HTML safely */}
      <Box
        fontSize="lg"
        fontWeight={500}
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(questionHtml),
        }}
      />

      <Box mt={4}>
        {/* === Essay Question === */}
        {questionType.id === ESSAY_ID && (
          <FormikFormControl
            label="Your Answer:"
            helperText={`${essayValue.length}/20,000 characters`}
            error={essayError}
          >
            <Textarea
              value={essayValue}
              onChange={handleEssayChange}
              maxLength={20000}
            />
          </FormikFormControl>
        )}

        {/* === Multiple Choice (Multiple Answers) === */}
        {questionType.id === MULTIPLE_CHOICE_ID && hasMultipleAnswer && (
          <CheckboxGroup
            key={uuidv4()}
            onChange={(values) => {
              handleSelectAnswer(question, values);
            }}
            value={selectedValue}
          >
            {parsedOptions
              ? parsedOptions.map((choice, idx) => (
                  <Box
                    key={getOptionKey(choice, idx)}
                    cursor="pointer"
                    onClick={() => {
                      handleCheckboxParentClick(question, choice.option);
                    }}
                    p={4}
                    mb={5}
                    border="1px"
                    borderColor={
                      selectedValue.includes(choice.option)
                        ? activeBorderColor
                        : borderColor
                    }
                    _hover={{
                      backgroundColor: hoverColor,
                      transition: 'background-color 0.3s ease',
                    }}
                    minH={35}
                  >
                    <Checkbox
                      value={choice.option}
                      colorScheme={optionsSelectColor}
                    >
                      {choice.value}
                    </Checkbox>
                  </Box>
                ))
              : renderOptionsError()}
          </CheckboxGroup>
        )}

        {/* === Multiple Choice (Single Answer) === */}
        {questionType.id === MULTIPLE_CHOICE_ID && !hasMultipleAnswer && (
          <RadioGroup
            key={uuidv4()}
            onChange={(value) => handleSelectAnswer(question, [value])}
            value={selectedValue[0]}
          >
            {parsedOptions
              ? parsedOptions.map((choice, idx) => (
                  <Box
                    key={getOptionKey(choice, idx)}
                    cursor="pointer"
                    onClick={() =>
                      selectedValue[0] === choice.option
                        ? handleSelectAnswer(question, [])
                        : handleSelectAnswer(question, [choice.option])
                    }
                    p={4}
                    mb={5}
                    border="1px"
                    borderColor={
                      selectedValue[0] === choice.option
                        ? activeBorderColor
                        : borderColor
                    }
                    _hover={{
                      backgroundColor: hoverColor,
                      transition: 'background-color 0.3s ease',
                    }}
                    minH={35}
                  >
                    <Radio
                      size="md"
                      value={choice.option}
                      colorScheme={optionsSelectColor}
                    >
                      {choice.value}
                    </Radio>
                  </Box>
                ))
              : renderOptionsError()}
          </RadioGroup>
        )}

        {/* === True or False === */}
        {questionType.id === TRUE_OR_FALSE_ID && (
          <RadioGroup
            onChange={(value) => handleSelectAnswer(question, value)}
            value={selectedValue[0] ?? ''}
          >
            <Box
              cursor="pointer"
              onClick={() =>
                selectedValue[0] === 'true'
                  ? handleSelectAnswer(question, [])
                  : handleSelectAnswer(question, ['true'])
              }
              p={4}
              border="1px"
              borderColor={
                selectedValue[0] === 'true' ? activeBorderColor : borderColor
              }
              _hover={{
                backgroundColor: hoverColor,
                transition: 'background-color 0.3s ease',
              }}
              minH={35}
            >
              <Radio size="md" value="true" colorScheme={optionsSelectColor}>
                True
              </Radio>
            </Box>
            <Box
              cursor="pointer"
              onClick={() =>
                selectedValue[0] === 'false'
                  ? handleSelectAnswer(question, [])
                  : handleSelectAnswer(question, ['false'])
              }
              p={4}
              mt={4}
              border="1px"
              borderColor={
                selectedValue[0] === 'false' ? activeBorderColor : borderColor
              }
              _hover={{
                backgroundColor: hoverColor,
                transition: 'background-color 0.3s ease',
              }}
              minH={35}
            >
              <Radio size="md" value="false" colorScheme={optionsSelectColor}>
                False
              </Radio>
            </Box>
          </RadioGroup>
        )}
      </Box>
    </Box>
  );
}

// Improved PropTypes for better documentation and validation
QuestionItem.propTypes = {
  position: PropTypes.number.isRequired,
  question: PropTypes.shape({
    questionHtml: PropTypes.string.isRequired,
    questionType: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
    hasMultipleAnswer: PropTypes.bool,
    multipleChoiceChoicesJson: PropTypes.string,
  }).isRequired,
  selectedValue: PropTypes.array.isRequired,
  handleSelectAnswer: PropTypes.func.isRequired,
};

export default QuestionItem;
